import React from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import { Form, Header, Segment, Button, Input, Icon, Popup, Transition, Loader, Grid } from 'semantic-ui-react';

import PremisesTable from './Premises/PremisesTable';
import PremisesFilter from './Premises/PremisesFilter';
import PremisesDetails from './Premises/PremisesDetails';
import RoomOrganizationalUnitPropertyForm from './Premises/RoomOrganizationalUnitProperty';
import PaginationSegment from './util/PaginationSegment';
import ResultsPerPageDropdown from './util/ResultsPerPageDropdown';
import { fetchDisabledAccesses, fetchRoomTypes } from './Lookups/util';
import { IconHeader, FiltersOnStyle, RightHeaderWithButton } from './util/css';
import { getTotalNumberOfPages, checkOrganizationalUnitsPrivilege, checkFaculty, checkWritePrivilege, formatListForDropdownOptions, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE } from './util/util';
import { fetchFaculties } from '../Lookups/util';

axios.defaults.xsrfHeaderName = "X-XSRF-TOKEN";
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;
//axios.defaults.crossDomain = true;
const ADD_ROOM_ORGANIZATIONAL_UNIT_PROPERTY = 1;
const EDIT_ROOM_ORGANIZATIONAL_UNIT_PROPERTY = 2;

class Premises extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            organizationalUnits: [],
            organizationalUnitId: null,
            activeRole: null,

            rooms: [],
            filteredRooms: [],

            lookups: {
                roomTypes: [],
                disabledAccesses: [],
                properties: [],
                organizationalUnitProperties: [],
            },

            // Form data
            roomName: '',
            roomCode: '',
            roomType: null,
            floor: '',
            area: '',
            disabledAccess: null,
            property: null,
            seatCapacity: '',
            examSeatCapacity: '',
            roomId: null,
            roomOptLock: null,
            room: {
                organizationalUnitProperties: [],
            },
            organizationalUnitProperties: [],
            organizationalUnitPropertyEditMode: false,

            // Component behaviour
            requestMode: null,
            loading: true,
            showFiltersOnButton: false,
            showFilterMode: false,

            // For details
            details: false,
            roomObject: null,

            // For required fields
            roomNameError: false,
            roomCodeError: false,
            roomTypeError: false,
            floorError: false,
            areaError: false,
            propertyError: false,
            disabledAccessError: false,

            // For pagination
            activePage: 1,
            totalPages: 1,
            resultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
            filterRequestData: null,

            // For sorting, default values
            fieldName: null,
            fieldProperty: null,
            direction: null,
        };

        this.sendRoomRequest = this.sendRoomRequest.bind(this);
        this.getRooms = this.getRooms.bind(this);
        this.updateRooms = this.updateRooms.bind(this);
        this.populateValues = this.populateValues.bind(this);
        this.resetValues = this.resetValues.bind(this);
        this.activateAddRequest = this.activateAddRequest.bind(this);
        this.activateEditRequest = this.activateEditRequest.bind(this);
        this.cancelRequest = this.cancelRequest.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);

        this.activateFilter = this.activateFilter.bind(this);
        this.onFilterData = this.onFilterData.bind(this);
        this.filtersOff = this.filtersOff.bind(this);
        this.getDataToRender = this.getDataToRender.bind(this);

        this.fetchLookups = this.fetchLookups.bind(this);
        this.getUserData = this.getUserData.bind(this);
        this.fetchDataForRole = this.fetchDataForRole.bind(this);

        this.switchOrganizationalUnitPropertyEditMode = this.switchOrganizationalUnitPropertyEditMode.bind(this);
        this.onAddNewOrganizationalUnitProperty = this.onAddNewOrganizationalUnitProperty.bind(this);
        this.onDeleteRoomOrganizationalUnitProperty = this.onDeleteRoomOrganizationalUnitProperty.bind(this);

        this.switchToDetails = this.switchToDetails.bind(this);
        this.switchToTable = this.switchToTable.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.getFilteredRooms = this.getFilteredRooms.bind(this);
        this.handleResultsPerPageChange = this.handleResultsPerPageChange.bind(this);
        this.getSortedRooms = this.getSortedRooms.bind(this);
    }

    componentDidMount() {
        this.getUserData();
    }

    getUserData() {
        axios.all([this.fetchActiveRole(), this.fetchUserOrganizationalUnit()])
            .then(axios.spread((activeRoleRes, userOrganizationalUnitRes) => {
                const activeRole = activeRoleRes.data;
                const organizationalUnitId = userOrganizationalUnitRes.data;

                this.fetchLookups(activeRole, organizationalUnitId);
            }))
            .catch(err => {
                this.setState({ error: true, loading: false });
                // console.log('Failed to fetch data.');
            });
    }

    fetchLookups(activeRole, organizationalUnitId) {
        axios.all([fetchDisabledAccesses(), fetchRoomTypes()])
            .then(axios.spread((disabledAccessesRes, roomTypesRes) => {
                const disabledAccesses = formatListForDropdownOptions(disabledAccessesRes.data);
                const roomTypes = formatListForDropdownOptions(roomTypesRes.data);

                const lookups = {
                    disabledAccesses,
                    roomTypes,
                };

                this.fetchDataForRole(activeRole, organizationalUnitId, lookups);
            }))
            .catch(err => {
                this.setState({ error: true, loading: false });
                // console.log('Failed to fetch data.');
            });
    }

    fetchDataForRole(activeRole, organizationalUnitId, lookups) {
        if (checkOrganizationalUnitsPrivilege(activeRole)) {
            axios.all([fetchFaculties(), this.fetchAllOrganizationalUnitProperties(), this.fetchAllRooms()])
                .then(axios.spread((organizationalUnitsRes, propertiesRes, roomsRes) => {
                    const organizationalUnits = formatListForDropdownOptions(organizationalUnitsRes.data);
                    const rooms = roomsRes.data.responseList;
                    // FIXME: Needs to extract data from Property controller, not OrganizationalUnitProperty
                    // NOTE: Should check the flow at the client side, problems with the table relations and visibility of rooms to other organizationalUnits
                    const properties = propertiesRes.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.property.name,
                    }));
                    const organizationalUnitProperties = propertiesRes.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.organizationalUnit.name + ' - ' + el.property.name,
                    }));

                    let totalPages = getTotalNumberOfPages(roomsRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                    this.setState({
                        organizationalUnits,
                        rooms,
                        loading: false,
                        totalPages,
                        lookups: {
                            ...lookups,
                            properties,
                            organizationalUnitProperties,
                        },
                        activeRole,
                        organizationalUnitId,
                    });
                })).catch(err => {
                    this.setState({ error: true, loading: false });
                    // console.log('Failed to fetch data: ', err);
                });
        } else if (checkFaculty(activeRole)) {
            axios.all([this.fetchOrganizationalUnitProperties(), this.fetchRooms()])
                .then(axios.spread((propertiesRes, roomsRes) => {
                    const rooms = roomsRes.data.responseList;
                    // FIXME: Needs to extract data from Property controller, not OrganizationalUnitProperty
                    // NOTE: Should check the flow at the client side, problems with the table relations and visibility of rooms to other organizationalUnits
                    const properties = propertiesRes.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.property.name,
                    }));
                    const organizationalUnitProperties = propertiesRes.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.organizationalUnit.name + ' - ' + el.property.name,
                    }));

                    let totalPages = getTotalNumberOfPages(roomsRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                    this.setState({
                        rooms,
                        loading: false,
                        totalPages,
                        lookups: {
                            ...lookups,
                            properties,
                            organizationalUnitProperties,
                        },
                        activeRole,
                        organizationalUnitId,
                    });
                })).catch(err => {
                    this.setState({ error: true, loading: false });
                    // console.log('Failed to fetch data: ', err);
                });
        }
    }

    fetchActiveRole() {
        return axios.get('/user/type');
    }

    fetchUserOrganizationalUnit() {
        return axios.get('/user/organizational-unit');
    }

    fetchOrganizationalUnitProperties() {
        return axios.get('/assets/organizational-unit-properties/');
    }

    fetchRooms(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };
        return axios.post('/assets/rooms/paginated/', { ...pagination });
    }

    fetchAllOrganizationalUnitProperties() {
        return axios.get('/assets/organizational-unit-properties/all/');
    }

    fetchAllRooms(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };

        return axios.post('/assets/rooms/all/', { ...pagination });
    }

    getRooms(page, resultsPerPage, sortingDTO = null) {
        const {
            activeRole, fieldName, fieldProperty, direction
        } = this.state;
        const pagination = {
            page,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : fieldProperty,
            order: sortingDTO ? sortingDTO.direction === 'ascending' : direction ? direction === 'ascending' : null,
        };

        if (checkOrganizationalUnitsPrivilege(activeRole)) {
            axios.post('/assets/rooms/all/', { ...pagination })
                .then((res) => {
                    this.updateRooms(page, resultsPerPage, res, sortingDTO);
                })
                .catch(err => {
                    // console.log('Failed to fetch data.');
                });
        } else if (checkFaculty(activeRole)) {
            axios.post('/assets/rooms/paginated/', { ...pagination })
                .then((res) => {
                    this.updateRooms(page, resultsPerPage, res, sortingDTO);
                })
                // TODO: Error-handling
                .catch(err => {
                    // console.log('Failed to fetch data.');
                });
        }
    }

    getFilteredRooms(page, resultsPerPage, sortingDTO = null) {
        const {
             fieldName, fieldProperty, direction
        } = this.state;
        const paginationDTO = {
            page,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : fieldProperty,
            order: sortingDTO ? sortingDTO.direction === 'ascending' : direction ? direction === 'ascending' : null,
        };
        let { filterRequestData } = this.state;
        filterRequestData = { ...filterRequestData, paginationDTO };

        axios.post('/assets/rooms/filter/', { ...filterRequestData })
            .then(res => {
                this.updateRooms(page, resultsPerPage, res, sortingDTO, true);
            })
            .catch(err => {
                // console.log('Failed to fetch filter results: ', err);
                toast('Došlo je do greške prilikom filtriranja!');
            });
    }

    getSortedRooms(fieldName, fieldProperty = null) {
        const sortingDTO = {
            direction: (this.state.fieldName === fieldName ? (this.state.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'),
            fieldName,
            fieldProperty
        };

        if (!this.state.showFiltersOnButton) {
            this.getRooms(this.state.activePage - 1, this.state.resultsPerPage, sortingDTO);
        } else {
            this.getFilteredRooms(this.state.activePage - 1, this.state.resultsPerPage, sortingDTO);
        }
    }


    updateRooms(page, resultsPerPage, res, sortingDTO, filtersOn = false) {
        const rooms = res.data.responseList;
        let totalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);

        this.setState(prevState => ({
            activePage: page + 1,
            resultsPerPage,
            rooms: filtersOn ? [] : rooms,
            filteredRooms: filtersOn ? rooms : [],
            totalPages,
            fieldName: sortingDTO ? sortingDTO.fieldName : prevState.fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : prevState.fieldProperty,
            direction: sortingDTO ? (sortingDTO.fieldName === prevState.fieldName ? (prevState.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending') : prevState.direction,
        }));

        if (this.state.requestMode === 'add') {
            toast('Zahtjev uspješno poslan!');
        } else if (this.state.requestMode === 'edit') {
            toast('Zahtjev uspješno uređen!');
        }

        this.resetValues(null);
    }

    onFilterData(premisesFilter) {
        const { activeRole, resultsPerPage } = this.state;
        let premisesFilterRequest = {
            ...premisesFilter,
            paginationDTO: {
                page: 0,
                resultsPerPage,
            },
        };

            // Making sure it is sending for the current logged in organizational unit
        if (checkFaculty(activeRole)) {
            premisesFilterRequest = {
                ...premisesFilterRequest,
                organizationalUnit: this.state.organizationalUnitId,
            };
        }

        axios.post('/assets/rooms/filter/', { ...premisesFilterRequest })
            .then(res => {
                const filterData = res.data.responseList;

                let totalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
                this.setState({
                    activePage: 1,
                    totalPages,
                    filteredRooms: filterData,
                    rooms: [],
                    showFiltersOnButton: true,
                    filterRequestData: premisesFilterRequest,
                    showFilterMode: false,
                });
            })
            .catch(err => {
                // console.log('Failed to fetch filter results');
                toast('Doslo je do greske!');
            });
    }


    onAddNewOrganizationalUnitProperty(roomOrganizationalUnitProperty, editStatus = EDIT_ROOM_ORGANIZATIONAL_UNIT_PROPERTY, roomId = null) {
        const roomOrganizationalUnitPropertyRequest = {
            ...roomOrganizationalUnitProperty,
            room: { id: roomId || this.state.roomId },
            active: 1,
            comment: null,
        };


        axios.post('/assets/rooms/organizational-unit-properties/', { ...roomOrganizationalUnitPropertyRequest })
            .then(response => {
                if (editStatus === EDIT_ROOM_ORGANIZATIONAL_UNIT_PROPERTY) {
                    axios.get(`/assets/rooms/organizational-unit-properties/${roomId || this.state.roomId}/`)
                        .then(res => {
                            const organizationalUnitProperties = res.data.map(el => ({
                                ...el, key: el.id, value: el.id, text: el.organizationalUnitProperty.organizationalUnit.name + ' - ' + el.organizationalUnitProperty.property.name,
                            }));

                            const roomData = {
                                organizationalUnitProperties
                            };

                            this.setState({
                                room: roomData,
                            });
                            toast('Uspješno dodana nova imovina!');
                        })
                        .catch(err => {
                            // console.log('Failed to fetch data.');
                            toast('Greška prilikom dobivanja imovine za prostoriju!');
                            // console.log(err);
                        });
                } else if (this.state.showFiltersOnButton) {
                    this.getFilteredRooms(this.state.activePage - 1, this.state.resultsPerPage);
                } else {
                    this.getRooms(this.state.activePage - 1, this.state.resultsPerPage);
                }
            })
            .catch(err => {
                // TODO: Handle the exception
                // console.log('Failed to insert data.');
                toast('Nije uspješno dodana nova imovina!');
                // console.log(err);
            });
    }

    getDataToRender() {
        if (this.state.showFiltersOnButton) {
            return this.state.filteredRooms;
        }
        return this.state.rooms;
    }

    sendRoomRequest() {
        const {
            roomName,
            roomCode,
            roomType,
            floor,
            area,
            disabledAccess,
            property,
            roomId,
            roomOptLock,
        } = this.state;
        if (roomName === null || roomName === '' || roomCode === null || roomCode === '' ||
            roomType === null || floor === null || floor === '' || area === '' ||
            area === null || property === null || disabledAccess === null) {
            this.setState({
                roomNameError: roomName === null || roomName === '',
                roomCodeError: roomCode === null || roomCode === '',
                roomTypeError: roomType === null,
                floorError: floor === null || floor === '',
                areaError: area === null || area === '',
                propertyError: property === null,
                disabledAccessError: disabledAccess === null,
            });

            toast('Nisu sva potrebna polja popunjena!');
            return;
        }

        const roomRequest = {
            name: roomName,
            roomType: roomType ? { id: roomType } : null,
            code: roomCode,
            area,
            disabledAccess: disabledAccess ? { id: disabledAccess } : null,
            property: property ? { id: property } : null,
            floor,
            seatCapacity: this.state.seatCapacity,
            examSeatCapacity: this.state.examSeatCapacity,
        };

        if (this.state.requestMode === 'add') {
            axios.post('/assets/rooms/', { ...roomRequest })
                .then(res => {
                    const room = res.data;
                    const roomOrganizationalUnitProperty = {
                        organizationalUnitProperty: { id: this.state.property }
                    };
                    this.onAddNewOrganizationalUnitProperty(roomOrganizationalUnitProperty, ADD_ROOM_ORGANIZATIONAL_UNIT_PROPERTY, room.id);
                })
                .catch(err => {
                    // console.log(err);
                    toast('Došlo je do greške!');
                });
        } else if (this.state.requestMode === 'edit') {
            const roomPutRequest = { ...roomRequest, id: roomId, optLock: roomOptLock };
            axios.put('/assets/rooms/', { ...roomPutRequest })
                .then(res => {
                    if (this.state.showFiltersOnButton) {
                        this.getFilteredRooms(this.state.activePage - 1, this.state.resultsPerPage);
                    } else {
                        this.getRooms(this.state.activePage - 1, this.state.resultsPerPage);
                    }
                })
                .catch(err => {
                    // console.log(err);
                    toast('Došlo je do greške!');
                });
        }
    }

    activateAddRequest() {
        this.resetValues('add');
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    activateEditRequest(room) {
        this.populateValues(room);
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    cancelRequest() {
        this.resetValues(null);
    }

    activateFilter() {
        this.setState({
            showFilterMode: !this.state.showFilterMode
        });
    }

    // TODO: Merge two setStates into one, figure out how
    filtersOff() {
        this.setState({
            showFilterMode: false, showFiltersOnButton: false,
        });
        this.getRooms(0, this.state.resultsPerPage);
    }

    resetValues(requestMode) {
        const { activeRole } = this.state;
        this.setState(prevState => ({
            roomName: '',
            roomCode: '',
            roomType: null,
            floor: '',
            area: '',
            disabledAccess: null,
            property: null,
            seatCapacity: '',
            examSeatCapacity: '',
            roomId: null,
            roomOptLock: null,
            room: {
                organizationalUnitProperties: [],
            },

            roomNameError: false,
            roomCodeError: false,
            roomTypeError: false,
            floorError: false,
            areaError: false,
            propertyError: false,
            disabledAccessError: false,

            organizationalUnitId: checkOrganizationalUnitsPrivilege(activeRole) ? null : prevState.organizationalUnitId,
            requestMode,
        }));
    }

    populateValues(room) {
        if (room) {
            axios.get(`/assets/rooms/organizational-unit-properties/${room.id}/`)
                .then(res => {
                    const organizationalUnitProperties = res.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.organizationalUnitProperty.organizationalUnit.name + ' - ' + el.organizationalUnitProperty.property.name,
                    }));

                    const roomData = {
                        organizationalUnitProperties
                    };

                    this.setState({
                        room: roomData,
                    });
                })
                .catch(err => {
                    // console.log('Failed to fetch data.');
                    // console.log(err);
                    this.resetValues();
                    this.setState({ requestMode: null });
                });
            this.setState({
                roomName: room.name,
                roomCode: room.code,
                roomType: !!room.roomType && room.roomType.id,
                floor: room.floor,
                area: room.area,
                disabledAccess: !!room.disabledAccess && room.disabledAccess.id,
                property: !!room.property && room.property.id,
                seatCapacity: room.seatCapacity,
                examSeatCapacity: room.examSeatCapacity,
                roomId: room.id,
                roomOptLock: room.optLock,
                requestMode: 'edit',
                roomNameError: false,
                roomCodeError: false,
                roomTypeError: false,
                floorError: false,
                areaError: false,
                propertyError: false,
                disabledAccessError: false,
            });
        } else {
            this.setState({ requestMode: null });
        }
    }

    switchToDetails(room) {
        // console.log(room);
        if (room !== null) {
            axios.get(`/assets/rooms/organizational-unit-properties/${room.id}/`)
                .then(res => {
                    const organizationalUnitProperties = res.data.map(el => ({
                        ...el, key: el.id, value: el.id, text: el.organizationalUnitProperty.organizationalUnit.name + ' - ' + el.organizationalUnitProperty.property.name,
                    }));

                    const roomData = {
                        organizationalUnitProperties
                    };

                    this.setState({
                        room: roomData,
                        details: true,
                        roomObject: room,
                    });
                })
                .catch(err => {
                    // console.log('Failed to fetch data.');
                    // console.log(err);
                });
        }
    }

    switchToTable() {
        this.setState({
            roomObject: null,
            details: false,
        });
    }

    switchOrganizationalUnitPropertyEditMode() {
        this.setState(prevState => ({ organizationalUnitPropertyEditMode: !prevState.organizationalUnitPropertyEditMode }));
    }

    handleInputChange(event, { name, value }) {
        this.setState({
            [name]: value !== '' ? value : null,
        });
    }

    onDeleteRoomOrganizationalUnitProperty(roomOrganizationalUnitPropertyId) {
        axios.delete(`/assets/rooms/organizational-unit-properties/${roomOrganizationalUnitPropertyId}/`)
            .then(res => {
                let room;
                let organizationalUnitProperties = this.state.room.organizationalUnitProperties.filter(el => el.id !== roomOrganizationalUnitPropertyId);

                room = {
                    organizationalUnitProperties,
                };

                this.setState({
                    room,
                });
                toast('Uspješno brisanje!');
            })
            .catch(err => {
                const msg = err && err.response && err.response.data ? err.response.data : '';
                if (msg.includes('ConstraintViolationException')) {
                    toast(<FormattedMessage id="assets.rooms.roomOrgUnitPropertyDeleteConstraintError"/>);
                } else {
                    toast(<FormattedMessage id="assets.rooms.deleteError"/>);
                }
            });
    }

    // FIXME: Add func parameters to handleChange functions
    handlePageChange(e, { activePage }) {
        if (activePage === this.state.activePage) {
            return;
        }

        if (this.state.showFiltersOnButton) {
            this.getFilteredRooms(activePage - 1, this.state.resultsPerPage);
        } else {
            this.getRooms(activePage - 1, this.state.resultsPerPage);
        }
    }

    handleResultsPerPageChange(e, { value }) {
        if (this.state.showFiltersOnButton) {
            this.getFilteredRooms(0, value);
            return;
        }

        this.getRooms(0, value);
    }

    render() {
        const {
            details,
            roomObject,
            room,
            fieldName,
            direction,
            activeRole
        } = this.state;
        const rooms = this.getDataToRender();
        const organizationalUnitPrivilege = checkOrganizationalUnitsPrivilege(activeRole);
        const writePrivilege = checkWritePrivilege(activeRole);

        if (this.state.error) return <div>Greška</div>;
        if (this.state.loading) return <div style={{ paddingTop: '100px', paddingBottom: '100px' }}><Loader active inline="centered" /></div>;
        if (details) {
            return (
                <PremisesDetails
                    room={roomObject}
                    organizationalUnitProperties={room.organizationalUnitProperties}
                    switchToTable={this.switchToTable}
                />
            );
        }
        return (
            <div>
                <Header as="h1">Prostorije</Header>
                <Transition.Group animation="slide up" duration={1000}>
                    {(this.state.requestMode === 'edit') &&
                    <Grid columns={2} className="stackable">
                        <Grid.Column>
                            <Segment.Group raised>
                                <Segment raised>
                                    <Header as="h3">
                          Dodavanje prostorije
                                    </Header>
                                </Segment>
                                <Segment raised>
                                    <Form noValidate>
                                        <Form.Group widths="equal">
                                            <Form.Field
                                                control={Input}
                                                label="Naziv prostorije"
                                                name="roomName"
                                                value={this.state.roomName}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite naziv prostorije"
                                                required
                                                error={this.state.roomNameError}
                                            />

                                            <Form.Field
                                                control={Input}
                                                label="Kod prostorije"
                                                name="roomCode"
                                                value={this.state.roomCode}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite kod prostorije"
                                                required
                                                error={this.state.roomCodeError}
                                            />

                                            <Form.Dropdown clearable
                                                label="Tip prostorije"
                                                placeholder="Izaberite tip"
                                                value={this.state.roomType}
                                                options={this.state.lookups.roomTypes}
                                                onChange={this.handleInputChange}
                                                name="roomType"
                                                search
                                                fluid
                                                selection
                                                required
                                                error={this.state.roomTypeError}
                                            />
                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field
                                                label="Sprat"
                                                type="number"
                                                control={Input}
                                                name="floor"
                                                value={this.state.floor}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite sprat"
                                                required
                                                error={this.state.floorError}
                                            />
                                            <Form.Field
                                                label="Površina (m²)"
                                                type="number"
                                                control={Input}
                                                name="area"
                                                value={this.state.area}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite površinu"
                                                required
                                                error={this.state.areaError}
                                            />

                                            <Form.Dropdown clearable
                                                label="Imovina"
                                                placeholder="Izaberite imovinu"
                                                value={this.state.property}
                                                options={this.state.lookups.properties}
                                                onChange={this.handleInputChange}
                                                name="property"
                                                search
                                                fluid
                                                selection
                                                required
                                                error={this.state.propertyError}
                                            />
                                        </Form.Group>

                                        <Form.Group widths="equal">
                                            <Form.Field
                                                label="Broj mjesta"
                                                type="number"
                                                control={Input}
                                                name="seatCapacity"
                                                value={this.state.seatCapacity}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite broj mjesta"
                                            />
                                            <Form.Field
                                                label="Broj mjesta za ispit"
                                                type="number"
                                                control={Input}
                                                name="examSeatCapacity"
                                                value={this.state.examSeatCapacity}
                                                onChange={this.handleInputChange}
                                                placeholder="Unesite broj mjesta za ispit"
                                            />
                                            <Form.Dropdown clearable
                                                label="Pristup invalidima"
                                                value={this.state.disabledAccess}
                                                onChange={this.handleInputChange}
                                                options={this.state.lookups.disabledAccesses}
                                                name="disabledAccess"
                                                placeholder="Izaberite pristup invalidima"
                                                search
                                                fluid
                                                selection
                                                required
                                                error={this.state.disabledAccessError}
                                            />
                                        </Form.Group>
                                        <Button
                                            color="blue"
                                            onClick={
                                                () => {
                                                    this.sendRoomRequest();
                                                }
                                            }
                                        >
                                            {this.state.requestMode === 'add' ? 'Pošalji' : 'Uredi'}
                                        </Button>
                                        <Button
                                            onClick={this.cancelRequest}
                                            basic
                                            color="blue"
                                        >Odustani</Button>
                                    </Form>
                                </Segment>

                            </Segment.Group>
                        </Grid.Column>

                        <Grid.Column>
                            <Segment.Group>
                                <Segment clearing>
                                    <Header as="h3">
                                Imovina organizacionih jedinica
                                        <Header style={IconHeader} floated="right" onClick={this.switchOrganizationalUnitPropertyEditMode}>
                                            <Button
                                                basic
                                                color="blue"
                                                size="small"
                                                style={RightHeaderWithButton}
                                                onClick={this.onNewSelection}
                                            >
                                                {!this.state.organizationalUnitPropertyEditMode ? 'Uredi' : 'Prekini'}
                                            </Button>
                                        </Header>

                                    </Header>
                                </Segment>
                                <RoomOrganizationalUnitPropertyForm
                                    organizationalUnitProperties={this.state.lookups.organizationalUnitProperties || []}
                                    room={this.state.room}
                                    editMode={this.state.organizationalUnitPropertyEditMode}
                                    onAddNewOrganizationalUnitProperty={this.onAddNewOrganizationalUnitProperty}
                                    onDeleteRoomOrganizationalUnitProperty={this.onDeleteRoomOrganizationalUnitProperty}
                                />
                            </Segment.Group>
                        </Grid.Column>
                    </Grid>}

                    {(this.state.requestMode === 'add') &&
                    <Segment.Group raised>
                        <Segment raised>
                            <Header as="h3">
                                {this.state.requestMode === 'add' ? 'Dodavanje nove prostorije' : 'Uređivanje prostorije'}
                            </Header>
                        </Segment>
                        <Segment raised>
                            <Form noValidate>
                                <Form.Group widths="equal">
                                    <Form.Field
                                        control={Input}
                                        label="Naziv prostorije"
                                        name="roomName"
                                        value={this.state.roomName}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite naziv prostorije"
                                        required
                                        error={this.state.roomNameError}
                                    />

                                    <Form.Field
                                        control={Input}
                                        label="Kod prostorije"
                                        name="roomCode"
                                        value={this.state.roomCode}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite kod prostorije"
                                        required
                                        error={this.state.roomCodeError}
                                    />

                                    <Form.Dropdown clearable
                                        label="Tip prostorije"
                                        placeholder="Izaberite tip"
                                        value={this.state.roomType}
                                        options={this.state.lookups.roomTypes}
                                        onChange={this.handleInputChange}
                                        name="roomType"
                                        search
                                        fluid
                                        selection
                                        required
                                        error={this.state.roomTypeError}
                                    />
                                </Form.Group>

                                <Form.Group widths="equal">
                                    <Form.Field
                                        label="Sprat"
                                        type="number"
                                        control={Input}
                                        name="floor"
                                        value={this.state.floor}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite sprat"
                                        required
                                        error={this.state.floorError}
                                    />
                                    <Form.Field
                                        label="Površina (m²)"
                                        type="number"
                                        control={Input}
                                        name="area"
                                        value={this.state.area}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite površinu"
                                        required
                                        error={this.state.areaError}
                                    />

                                    <Form.Dropdown clearable
                                        label="Imovina"
                                        placeholder="Izaberite imovinu"
                                        value={this.state.property}
                                        options={this.state.lookups.properties}
                                        onChange={this.handleInputChange}
                                        name="property"
                                        search
                                        fluid
                                        required
                                        selection
                                        error={this.state.propertyError}
                                    />
                                </Form.Group>

                                <Form.Group widths="equal">
                                    <Form.Field
                                        label="Broj mjesta"
                                        type="number"
                                        control={Input}
                                        name="seatCapacity"
                                        value={this.state.seatCapacity}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite broj mjesta"
                                    />
                                    <Form.Field
                                        label="Broj mjesta za ispit"
                                        type="number"
                                        control={Input}
                                        name="examSeatCapacity"
                                        value={this.state.examSeatCapacity}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite broj mjesta za ispit"
                                    />
                                    <Form.Dropdown clearable
                                        label="Pristup invalidima"
                                        value={this.state.disabledAccess}
                                        onChange={this.handleInputChange}
                                        options={this.state.lookups.disabledAccesses}
                                        name="disabledAccess"
                                        placeholder="Izaberite pristup invalidima"
                                        search
                                        fluid
                                        selection
                                        required
                                        error={this.state.disabledAccessError}
                                    />
                                </Form.Group>
                                <Button
                                    color="blue"
                                    onClick={
                                        () => {
                                            this.sendRoomRequest();
                                        }
                                    }
                                >
                                Pošalji
                                </Button>
                                <Button
                                    onClick={this.cancelRequest}
                                    basic
                                    color="blue"
                                >Odustani</Button>
                            </Form>
                        </Segment>

                    </Segment.Group>
                    }
                </Transition.Group>
                <Segment.Group raised>
                    <Segment>
                        <Header as="h3">
                        Spisak prostorija
                            <Header style={IconHeader} floated="right" >
                                {this.state.showFiltersOnButton &&
                                <Button
                                    floated="left"
                                    icon
                                    labelPosition="right"
                                    color="orange"
                                    size="small"
                                    style={FiltersOnStyle}
                                >
                                    <Icon className="remove" onClick={this.filtersOff} />
                                Filter uključen
                                </Button>}
                                <Icon />
                                <Popup
                                    trigger={<Icon link circular basic="true" floated="right" name="filter" onClick={this.activateFilter} />}
                                    content="Filtriraj podatke"
                                    hideOnScroll
                                    position="top center"
                                />
                            </Header>
                        </Header>
                    </Segment>
                    {this.state.showFilterMode &&
                    <PremisesFilter
                        onFilterData={this.onFilterData}
                        lookups={this.state.lookups}
                        roomTypes={this.state.lookups.roomTypes}
                        properties={this.state.lookups.properties}
                        disabledAccesses={this.state.lookups.disabledAccesses}
                        organizationalUnits={this.state.organizationalUnits}
                        organizationalUnitPrivilege={organizationalUnitPrivilege}
                        cancelFilter={this.activateFilter}
                    />
                    }
                    <Segment>
                        {writePrivilege &&
                        <React.Fragment>
                            <Button
                                floated="left"
                                icon
                                labelPosition="left"
                                primary
                                size="small"
                                onClick={this.activateAddRequest}
                            >
                                <Icon className="file text outline icon" />
                            Dodavanje nove prostorije
                            </Button>
                            <ResultsPerPageDropdown
                                resultsPerPage={this.state.resultsPerPage}
                                onResultsPerPageChange={this.handleResultsPerPageChange}
                            />
                            <br />
                            <br />
                        </React.Fragment>
                        }
                        {rooms && rooms.length ?
                            <PremisesTable
                                rooms={rooms}
                                activateEditRequest={this.activateEditRequest}
                                writePrivilege={writePrivilege}
                                switchToDetails={this.switchToDetails}
                                getSortedRooms={this.getSortedRooms}
                                fieldName={fieldName}
                                direction={direction}
                            /> :
                            <p>Nema podataka za prikaz.</p>
                        }
                    </Segment>
                    <PaginationSegment
                        activePage={this.state.activePage}
                        totalPages={this.state.totalPages}
                        onPageChange={this.handlePageChange}
                    />
                    <ToastContainer hideProgressBar />
                </Segment.Group>
            </div>
        );
    }
}


export default Premises;



// WEBPACK FOOTER //
// ./src/views/Assets/Premises.js